import { computed, isReactive, reactive, Ref, set } from '@vue/composition-api'

export type Getter = (key: string) => any

export type Setter = (key: string, value: any) => void

export type GetterContext = {
  getters: Store['getters']
  get: Getter
}

export type MutationContext = GetterContext & {
  set: Setter
}

export type ActionContext = MutationContext & {
  commit: Store['commit']
  dispatch: Store['dispatch']
}

export type GetterFn = (this: Store, ctx: GetterContext) => any

export type MutationFn = (this: Store, ctx: MutationContext, ...args: any[]) => any

export type ActionFn = (this: Store, ctx: ActionContext, payload: any) => any

export type Query = (...args: any[]) => Ref<any>

export type QueryFn = (this: Store, ctx: GetterContext, ...args: any[]) => any

export type GetterTree = {
  [key: string]: GetterFn
}

export type MutationTree = {
  [key: string]: MutationFn
}

export type ActionTree = {
  [key: string]: ActionFn
}

export type QueryTree = {
  [key: string]: QueryFn
}

export type StoreOptions = {
  state?: any
  getters?: GetterTree
  actions?: ActionTree
  mutations?: MutationTree
  queries?: QueryTree
}

export class Store {
  public state: Record<string, any> = Object.create(null)
  private mutations: object = Object.create(null)
  private actions: object = Object.create(null)
  public getters: Record<string, Ref> = Object.create(null)
  public queries: Record<string, Query> = Object.create(null)

  private initState() {
    if (isReactive(this.state)) return
    this.state = reactive(this.state)
  }

  defineStore(options: StoreOptions) {
    this.initState()
    if (options.state != null) {
      for (const [key, value] of Object.entries(options.state)) {
        this.defineState(key, value)
      }
    }

    if (options.getters != null) {
      for (const [key, value] of Object.entries(options.getters)) {
        this.makeGetter(key, value)
      }
    }

    if (options.mutations != null) {
      for (const [key, value] of Object.entries(options.mutations)) {
        this.registerMutation(key, value)
      }
    }

    if (options.actions != null) {
      for (const [key, value] of Object.entries(options.actions)) {
        this.registerAction(key, value)
      }
    }

    if (options.queries != null) {
      for (const [key, value] of Object.entries(options.queries)) {
        this.makeQuery(key, value)
      }
    }
  }

  defineState(key: string, defaultValue: any) {
    this.initState()
    if (key in this.state) {
      throw new Error('key already exists')
    }
    set(this.state, key, defaultValue)
  }

  getState(key: string) {
    this.initState()
    if (!(key in this.state)) {
      throw new Error('key doesnt exist')
    }
    return this.state[key]
  }

  setState(key: string, value: any) {
    this.initState()
    if (!(key in this.state)) {
      throw new Error('key doesnt exist')
    }
    this.state[key] = value
  }

  registerMutation(key: string, handler: MutationFn) {
    const ctx: MutationContext = {
      getters: this.getters,
      get: this.getState.bind(this),
      set: this.setState.bind(this)
    }
    this.mutations[key] = (function wrappedMutation(this: Store, args: any[]) {
      return handler.call(
        this,
        ctx,
        ...args
      )
    }).bind(this)
  }

  registerAction(key: string, handler: ActionFn) {
    const ctx: ActionContext = {
      getters: this.getters,
      get: this.getState.bind(this),
      set: this.setState.bind(this),
      commit: this.commit.bind(this),
      dispatch: this.dispatch.bind(this)
    }
    this.actions[key] = (function wrappedAction(this: Store, payload: any) {
      return handler.call(this, ctx, payload)
    }).bind(this)
  }

  makeGetter(key: string, handler: GetterFn) {
    this.getters[key] = computed(
      () => {
        return handler.call(this, {
          getters: this.getters,
          get: this.getState.bind(this)
        })
      }
    )
  }

  makeQuery(key: string, handler: QueryFn) {
    this.queries[key] = (...args) => computed(
      () => handler.call(this, {
        getters: this.getters,
        get: this.getState.bind(this)
      }, ...args)
    )
  }

  commit(key: string, ...args: any[]) {
    return this.mutations[key](args)
  }

  dispatch(key: string, payload?: any) {
    return this.actions[key](payload)
  }
}
